import React from 'react';

import { createRoot, Root } from 'react-dom/client';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';

import { PcoStubNestedWC } from './PcoStubNestedWC';

class PcoStubNestedElement extends HTMLElement {
  private readonly shadow: ShadowRoot;
  private reactRoot?: Root;
  private emotionCache?: ReturnType<typeof createCache>;

  static get observedAttributes() {
    return ['version'];
  }

  get version() {
    return this.getAttribute('version') ?? '0.0.1';
  }

  set version(value: string) {
    this.setAttribute('version', value);
  }

  private _updateVersion?: (version: string) => void;

  get updateVersion() {
    return this._updateVersion;
  }

  set updateVersion(value: ((version: string) => void) | undefined) {
    this._updateVersion = value;
    this.render();
  }

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    const reactRootElement = document.createElement('div');
    this.shadow.appendChild(reactRootElement);

    this.emotionCache = createCache({
      key: 'pco-product-service-nested',
      container: this.shadow,
      insertionPoint: reactRootElement,
      prepend: true,
    });

    this.reactRoot = createRoot(reactRootElement);
    this.render();
  }

  disconnectedCallback() {
    this.reactRoot?.unmount();
    this.shadow.replaceChildren();
    this.reactRoot = undefined;
    this.emotionCache = undefined;
  }

  attributeChangedCallback() {
    this.render();
  }

  private render() {
    if (!this.isConnected || !this.reactRoot || !this.emotionCache) return;

    this.reactRoot.render(
      <CacheProvider value={this.emotionCache}>
        <PcoStubNestedWC version={this.version} updateVersion={this.updateVersion} />
      </CacheProvider>,
    );
  }
}

defineCustomElement('pco-stub-element-nested', PcoStubNestedElement);

function defineCustomElement(name: string, constructor: CustomElementConstructor) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
